import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, Card, CardTitle, Field, Input, useToast } from "@/core/ui";
import { ApiError } from "@/core/api/client";
import { hasRole } from "@/core/auth/types";
import { useSession } from "@/core/auth/useSession";
import {
  usePuzzleTransition,
  useSchedule,
  type BacklogPuzzle,
  type DefinitionErrorItem,
  type PublicationInfo,
} from "./api";
import { StatusBadge, useStatusLabel } from "./status";

/** `datetime-local` works in local time; the API speaks ISO 8601 in UTC. */
export function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

export function fromLocalInput(value: string): string {
  return new Date(value).toISOString();
}

export interface WorkflowCardProps {
  puzzle: BacklogPuzzle;
  publication: PublicationInfo | null;
  onDefinitionErrors: (errors: DefinitionErrorItem[]) => void;
}

export function WorkflowCard({ puzzle, publication, onDefinitionErrors }: WorkflowCardProps) {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const session = useSession();
  const statusLabel = useStatusLabel();
  const move = usePuzzleTransition(puzzle.slug);
  const schedule = useSchedule(puzzle.slug);
  const [startsAt, setStartsAt] = useState(() => toLocalInput(publication?.starts_at));
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const isAdmin = hasRole(session.data?.user, "tenant_admin");

  useEffect(() => {
    setStartsAt(toLocalInput(publication?.starts_at));
  }, [publication?.starts_at]);

  const run = (target: BacklogPuzzle["status"]) => {
    setErrorCode(null);
    move.mutate(
      {
        target,
        starts_at: target === "scheduled" && startsAt ? fromLocalInput(startsAt) : undefined,
      },
      {
        onSuccess: () => toast(statusLabel(target), "success"),
        onError: (error) => {
          if (error instanceof ApiError) {
            setErrorCode(error.code);
            const errors = error.payload["errors"];
            if (error.code === "invalid_definition" && Array.isArray(errors)) {
              onDefinitionErrors(errors as DefinitionErrorItem[]);
            }
          } else {
            setErrorCode("unknown");
          }
        },
      },
    );
  };

  const submitSchedule = (event: FormEvent) => {
    event.preventDefault();
    if (!startsAt) return;
    schedule.mutate(
      { starts_at: fromLocalInput(startsAt) },
      {
        onSuccess: () => toast(t("admin.saved"), "success"),
        onError: () => toast(t("app.error_generic"), "danger"),
      },
    );
  };

  return (
    <Card>
      <CardTitle>{t("backlog.workflow.title")}</CardTitle>
      <p className="bt-workflow__status">
        <StatusBadge status={puzzle.status} />
      </p>
      <div className="bt-workflow__transitions">
        {puzzle.available_transitions.length === 0 ? (
          <p className="bt-workflow__empty">{t("backlog.workflow.none")}</p>
        ) : null}
        {puzzle.available_transitions.map((target) => (
          <Button
            key={target}
            size="sm"
            variant={target === "published" ? "primary" : "outline"}
            loading={move.isPending}
            onClick={() => run(target)}
          >
            {t("backlog.workflow.move_to", { status: statusLabel(target) })}
          </Button>
        ))}
      </div>
      <div aria-live="polite" className="bt-workflow__error">
        {errorCode ? (
          <Alert tone="danger">
            {t(`backlog.errors.${errorCode}`, { defaultValue: t("app.error_generic") })}
          </Alert>
        ) : null}
      </div>

      {isAdmin ? (
        <form onSubmit={submitSchedule} className="bt-workflow__schedule">
          <h3 className="bt-workflow__schedule-title">{t("backlog.schedule.title")}</h3>
          <Field
            label={t("backlog.schedule.starts_at")}
            htmlFor="schedule-starts-at"
            hint={t("backlog.schedule.hint")}
          >
            <Input
              id="schedule-starts-at"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
            />
          </Field>
          <Button type="submit" variant="secondary" size="sm" loading={schedule.isPending}>
            {t("backlog.schedule.save")}
          </Button>
          {publication?.published_at ? (
            <p className="bt-workflow__published-at">
              {t("backlog.schedule.published_at", {
                date: new Date(publication.published_at).toLocaleString(i18n.language),
              })}
            </p>
          ) : null}
        </form>
      ) : null}
    </Card>
  );
}
